import { z } from 'zod';

/**
 * Strongly-typed parameter schema from backend.
 * Pricing parameters may be decimal, string, or boolean.
 */
const numberOrString = z.union([z.number(), z.string()]);

export type ParamType = 'decimal' | 'string' | 'boolean';

/** Single choice option from backend: value (for API), label (for display), and optional description. */
export const ParameterChoiceSchema = z.object({
  value: z.string(),
  label: z.string(),
  description: z.string().optional(),
});

export type ParameterChoice = z.infer<typeof ParameterChoiceSchema>;

export function formatParameterChoiceLabel(choice: ParameterChoice): string {
  const { label, description } = choice;
  return description ? `${label} (${description})` : label;
}

export const ParameterSchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string().optional().default(''),
  required: z.boolean(),
  param_type: z
    .enum(['decimal', 'string', 'boolean'])
    .optional()
    .default('decimal'),
  resolved_default: z
    .union([numberOrString, z.boolean()])
    .nullable()
    .optional(),
  minimum: numberOrString.nullable(),
  maximum: numberOrString.nullable(),
  choices: z.array(ParameterChoiceSchema).optional().nullable(),
});

export type ParameterSchema = z.infer<typeof ParameterSchema>;

export const PricingModel = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  function_key: z.string(),
  is_active: z.boolean(),
  parameters: z.record(z.string(), z.string().nullable()),
  output_quantity: z.boolean(),
  output_unit_cost: z.boolean(),
  created_date: z.string(),
  updated_date: z.string(),
  available_functions: z.array(z.string()).optional(),
  parameter_schemas: z.record(z.string(), ParameterSchema),
});

export type PricingModel = z.infer<typeof PricingModel>;

export type PricingModelPayload = {
  name: string;
  description: string;
  function_key: string;
  parameters: Record<string, string | null>;
  is_active?: boolean;
};
